import { useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Add, DeleteOutline } from '@mui/icons-material';
import type { PSDGeneral, PSDHydroDetail } from '../../../types/psd';
import { computeHydroRowLive } from './computations';
import { useTestFormulas } from '../../../hooks/useTestFormulas';
import { FormulaHeaderTooltip } from '../components/FormulaHeaderTooltip';

interface Props {
  general: PSDGeneral;
  rows: PSDHydroDetail[];
  onChange: (next: PSDHydroDetail[]) => void;
  readOnly?: boolean;
}

const cellSx = {
  '& .MuiInputBase-input': {
    py: 0.4, px: 0.5, fontSize: '0.75rem', textAlign: 'right' as const,
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none', margin: 0,
    },
    MozAppearance: 'textfield',
  },
};

const fmt = (v: number | null, d = 4): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : v.toFixed(d);

export const HydroReadingsGrid = ({ general, rows, onChange, readOnly = false }: Props) => {
  const theme = useTheme();
  const { formulaFor } = useTestFormulas('psd');

  // Keep rows in insertion order during data entry — re-sorting on every
  // keystroke makes the focused cell jump rows. The backend repository
  // returns rows ORDER BY ElapsedTimeMin on reload, so a fresh page load
  // still presents them in ascending time order.
  const computed = useMemo(
    () => rows.map((r) => computeHydroRowLive(r, general)),
    [rows, general],
  );

  const setField = (idx: number, field: keyof PSDHydroDetail, raw: string) => {
    const next = rows.slice();
    let v: string | number | null = raw;
    if (field === 'elapsedTimeMin' || field === 'hydrometerReadingR' || field === 'temperatureC') {
      v = raw === '' ? null : Number(raw);
    } else if (raw === '') {
      v = null;
    }
    next[idx] = { ...next[idx], [field]: v };
    onChange(next);
  };

  const addRow = () => {
    // Pick the smallest preset greater than the current max elapsed time so
    // the new row appears at the bottom of the table. If every preset is at
    // or below the current max, fall back to max + 5.
    const presets = [1, 2, 5, 15, 30, 60, 250, 1440];
    const existingTimes = rows
      .map((r) => r.elapsedTimeMin)
      .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
    const maxExisting = existingTimes.length === 0 ? -Infinity : Math.max(...existingTimes);
    const nextTime = presets.find((p) => p > maxExisting)
      ?? (maxExisting === -Infinity ? 1 : maxExisting + 5);
    onChange([...rows, {
      elapsedTimeMin: nextTime,
      readingDate: null, readingTime: null,
      hydrometerReadingR: null, temperatureC: null,
      computedDynamicViscosity: null, computedKConstant: null,
      computedModifiedReadingRm: null, computedEffectiveDepthLcm: null,
      computedParticleDiameterMm: null, computedPercentPassingWhole: null,
    }]);
  };

  const removeRow = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx));
  };

  return (
    <Paper elevation={0} sx={{
      p: 1.5, borderRadius: 2,
      border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.7rem', letterSpacing: 0.6 }}>
          Hydrometer Test
        </Typography>
        <Typography variant="caption" sx={{ ml: 1.5, color: 'text.secondary', fontSize: '0.7rem' }}>
          {rows.length} reading{rows.length === 1 ? '' : 's'} — values update as you type
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Button size="small" startIcon={<Add />} onClick={addRow} disabled={readOnly}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Add Reading Row
          </Button>
        </Box>
      </Box>

      {!general.hydrometerEnabled && (
        <Box sx={{
          p: 2, mb: 1, borderRadius: 1,
          backgroundColor: alpha(theme.palette.warning.light, 0.08),
          border: `1px dashed ${alpha(theme.palette.warning.main, 0.4)}`,
        }}>
          <Typography variant="caption" color="warning.dark">
            ⚠ Hydrometer is disabled. Enable it under Test Parameters → Hydrometer Configuration to record readings.
          </Typography>
        </Box>
      )}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '40px 80px 80px 110px 70px 90px 90px 90px 100px 36px',
        rowGap: 0.4, columnGap: 0.5, alignItems: 'center',
      }}>
        <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'center' }}>#</Box>
        <FormulaHeaderTooltip label="Elapsed t (min)" formula={formulaFor('elapsedTimeMin')?.formula} note={formulaFor('elapsedTimeMin')?.note}>
          <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Elapsed t<br/>(min)</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="Reading R" formula={formulaFor('hydrometerReadingR')?.formula} note={formulaFor('hydrometerReadingR')?.note}>
          <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Reading R</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="Temp T (°C)" formula={formulaFor('temperatureC')?.formula} note={formulaFor('temperatureC')?.note}>
          <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Temp T (°C)</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="η (mPa·s)" formula={formulaFor('dynamicViscosity')?.formula} note={formulaFor('dynamicViscosity')?.note}>
          <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>η (mPa·s)</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="K" formula={formulaFor('kConstant')?.formula} note={formulaFor('kConstant')?.note}>
          <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>K</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="Rm" formula={formulaFor('modifiedReadingRm')?.formula} note={formulaFor('modifiedReadingRm')?.note}>
          <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Rm</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="L (cm)" formula={formulaFor('effectiveDepthLcm')?.formula} note={formulaFor('effectiveDepthLcm')?.note}>
          <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>L (cm)</Box>
        </FormulaHeaderTooltip>
        <FormulaHeaderTooltip label="D (mm) | %P" formula={formulaFor('particleDiameterMm')?.formula} note={formulaFor('particleDiameterMm')?.note}>
          <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>D (mm) | %P</Box>
        </FormulaHeaderTooltip>
        <Box />

        {rows.map((row, idx) => {
          const c = computed[idx];
          return (
            <RowCells
              key={row.psdHydroDetailKey ?? `new-${idx}`}
              row={row} idx={idx} c={c}
              cellSx={cellSx} fmt={fmt}
              readOnly={readOnly}
              onFieldChange={setField}
              onRemove={removeRow}
            />
          );
        })}

        {rows.length === 0 && (
          <Box sx={{ gridColumn: '1 / -1', py: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.78rem' }}>
            No hydrometer readings yet. Click "Add Reading Row" to start.
          </Box>
        )}
      </Box>
    </Paper>
  );
};

interface RowCellsProps {
  row: PSDHydroDetail;
  idx: number;
  c: { dynamicViscosity: number | null; kConstant: number | null; modifiedReadingRm: number | null;
       effectiveDepthLcm: number | null; particleDiameterMm: number | null; percentPassingWhole: number | null };
  cellSx: object;
  fmt: (v: number | null, d?: number) => string;
  readOnly: boolean;
  onFieldChange: (idx: number, field: keyof PSDHydroDetail, raw: string) => void;
  onRemove: (idx: number) => void;
}

const RowCells = ({ row, idx, c, cellSx, fmt, readOnly, onFieldChange, onRemove }: RowCellsProps) => (
  <>
    <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', textAlign: 'center' }}>{idx + 1}</Box>
    <TextField type="number" size="small" sx={cellSx}
      value={row.elapsedTimeMin ?? ''}
      onChange={(e) => onFieldChange(idx, 'elapsedTimeMin', e.target.value)}
      disabled={readOnly}
    />
    <TextField type="number" size="small" sx={cellSx}
      value={row.hydrometerReadingR ?? ''}
      onChange={(e) => onFieldChange(idx, 'hydrometerReadingR', e.target.value)}
      disabled={readOnly}
    />
    <TextField type="number" size="small" sx={cellSx}
      value={row.temperatureC ?? ''}
      onChange={(e) => onFieldChange(idx, 'temperatureC', e.target.value)}
      disabled={readOnly}
    />
    <Box sx={{ fontSize: '0.72rem', textAlign: 'right', pr: 0.5, color: 'text.secondary' }}>
      {fmt(c.dynamicViscosity, 3)}
    </Box>
    <Box sx={{ fontSize: '0.72rem', textAlign: 'right', pr: 0.5, color: 'text.secondary' }}>
      {fmt(c.kConstant, 4)}
    </Box>
    <Box sx={{ fontSize: '0.72rem', textAlign: 'right', pr: 0.5, color: 'text.secondary' }}>
      {fmt(c.modifiedReadingRm, 2)}
    </Box>
    <Box sx={{ fontSize: '0.72rem', textAlign: 'right', pr: 0.5, color: 'text.secondary' }}>
      {fmt(c.effectiveDepthLcm, 2)}
    </Box>
    <Box sx={{ fontSize: '0.72rem', textAlign: 'right', pr: 0.5, fontWeight: 600 }}>
      <Tooltip title={`D=${fmt(c.particleDiameterMm, 4)}mm, P=${fmt(c.percentPassingWhole, 1)}%`} placement="left">
        <span>{fmt(c.particleDiameterMm, 4)} | {fmt(c.percentPassingWhole, 1)}</span>
      </Tooltip>
    </Box>
    <Box>
      <Tooltip title="Remove">
        {/* tabIndex={-1}: keep Tab flowing input → input across rows for data entry. */}
        <IconButton size="small" color="error" onClick={() => onRemove(idx)} disabled={readOnly} tabIndex={-1}>
          <DeleteOutline sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  </>
);
